import { Service } from 'typedi';
import { action, makeObservable, observable } from 'mobx';
import { UsersService } from '../../services/UsersService';
import { UserEntity } from '../../core/entity/UserEntity';
import { Notifications } from '../Notifications';
import { FeatureEntity, FeatureService } from '../../services/FeatureService';
import { AnalyticsAccessService } from '../../services/AnalyticsAccessService';
import { AnalyticsAccessEntity, AnalyticsAccessType } from '../../core/entity/AnalyticsAccessEntity';
import i18n from '../../i18n';

@Service()
export default class UsersPageStore {
    @observable isLoaded: boolean = false;

    @observable users: UserEntity[] = [];

    @observable user?: UserEntity;
    @observable features: FeatureEntity[] = [];
    /** Full `analytics_access` catalog (pricing tiers + feature access definitions). */
    @observable analyticsAccessCatalog: AnalyticsAccessEntity[] = [];
    /** Current user’s linked analytics access rows (edit form). */
    @observable userAnalyticsAccess: AnalyticsAccessEntity[] = [];

    /** `PRICING_TIER` row for the org selected in the user edit form (read-only; from organization analytics access). */
    @observable organizationPricingTier?: AnalyticsAccessEntity;
    @observable organizationPricingTierLoading: boolean = false;

    constructor(private service: UsersService, private featureService: FeatureService, private analyticsAccessService: AnalyticsAccessService) {
        makeObservable(this);
    }

    load() {
        this.getUsers().then(() => {
            this.isLoaded = true;
        });
    }

    @action updateUser(id: string, roles: string[], isActive?: boolean, organizationId?: string) {
        return this.service
            .updateUser(id, roles, isActive, organizationId)
            .then(res => {
                this.users = this.users.map(u => {
                    if (u.id === res.id) {
                        return res;
                    }
                    return u;
                });
                Notifications.success(i18n.t('common:saveUserSuccess', 'User successfully saved'));
                return res;
            })
            .catch(e => {
                Notifications.fromException(e);
                throw e;
            });
    }

    @action async saveFeatures(u: UserEntity | string, features: FeatureEntity[]) {
        await this.featureService.setUserFeatures(features, u);
        Notifications.success(`Features successfully saved users`);
    }

    @action
    async saveUserAnalyticsAccess(userId: string, analyticsAccessIds: number[]) {
        const updated = await this.analyticsAccessService.setUserAccess(userId, analyticsAccessIds);
        if (this.user?.id === userId) {
            this.userAnalyticsAccess = updated;
        }
        Notifications.success(`Analitycs Access successfully saved users`);

        return updated;
    }

    @action
    async fetchAnalyticsAccessCatalog() {
        if (!this.analyticsAccessCatalog?.length) {
            this.analyticsAccessCatalog = await this.analyticsAccessService.getAll();
        }
    }

    @action
    async loadOrganizationPricingTier(organizationId?: string) {
        if (!organizationId) {
            this.organizationPricingTierLoading = false;
            return;
        }

        this.organizationPricingTierLoading = true;

        try {
            const access = await this.analyticsAccessService.getOrganizationAccess(organizationId);
            this.organizationPricingTier = access.find(a => a.type === AnalyticsAccessType.PricingTier);
        } catch (e) {
            Notifications.fromException(e);
        } finally {
            this.organizationPricingTierLoading = false;
        }
    }

    @action
    async fetchUser(id: string) {
        try {
            const [user, userFeatures, userAccess] = await Promise.all([
                this.service.getUserById(id),
                this.featureService.getUserFeatures(id),
                this.analyticsAccessService.getUserAccess(id),
            ]);
            this.user = user;
            this.features = userFeatures;
            this.userAnalyticsAccess = userAccess;
            await this.fetchAnalyticsAccessCatalog();
        } catch (e) {
            Notifications.fromException(e);
            throw e;
        }
        return this.user;
    }

    @action async getUsers() {
        const users = await this.service.getUsers();
        this.users = users.sort((a, b) => a.username.localeCompare(b.username));
        return this.users;
    }

    @action dispose() {
        this.users = [];
        this.userAnalyticsAccess = [];
        this.analyticsAccessCatalog = [];
        this.organizationPricingTier = undefined;
        this.organizationPricingTierLoading = false;
    }
}
